import { ArrowLeft, Clock3, Gauge, RefreshCw, Star } from 'lucide-react'
import { useState } from 'react'

import { QueryState } from '@krp/sale-ui'
import { Button, DiagnosticCopy } from '@krp/ui-kit'

import '../../../shared/ui/aqsi.css'
import { SEGMENT_MARKS, SEGMENT_WORDS } from '../model/words'
import type { TraceSaleVm, TraceScreenActions, TraceScreenVm, TraceToolsVm, TraceTrendVm } from '../model/types'
import './trace.css'

/**
 * «Диагностика продаж» (RP-SALE-TRACE): последние продажи кабинета и разбор выбранной — где ушло время чека. Окно для
 * администратора с правом `sales.trace`; кассиру оно не показывается. Участок различается цветом, буквой-знаком и словом:
 * экран в оттенках серого остаётся читаемым.
 */
export function TraceScreen({ vm, actions }: { vm: TraceScreenVm; actions: TraceScreenActions }) {
  return (
    <section className="aqsi-screen aqsi-trace" aria-labelledby="aqsi-trace-title">
      <header className="aqsi-toolbar">
        <h2 id="aqsi-trace-title">Диагностика продаж</h2>
        <Button icon={<RefreshCw size={14} aria-hidden="true" />} busy={vm.list.query.refreshing} onClick={actions.reloadList}>
          Обновить список
        </Button>
      </header>
      <p className="aqsi-caption">
        Последние продажи кабинета и то, где в каждой ушло время: браузер и интернет кассира, наш сервер, доставка кабинет → касса,
        терминал и покупатель, чек на кассе.
      </p>
      <TraceTools tools={vm.tools} actions={actions} />
      {vm.sale === null ? (
        <>
          <TraceTrend trend={vm.trend} />
          <TraceList vm={vm} actions={actions} />
        </>
      ) : (
        <TraceSale sale={vm.sale} actions={actions} />
      )}
    </section>
  )
}

function TraceTools({ tools, actions }: { tools: TraceToolsVm; actions: TraceScreenActions }) {
  const [confirmCheck, setConfirmCheck] = useState(false)
  const { deep, check } = tools
  return (
    <div className="aqsi-trace__tools">
      <section className="aqsi-panel" aria-labelledby="aqsi-trace-deep">
        <h3 id="aqsi-trace-deep" className="aqsi-trace__heading">
          Подробный режим
        </h3>
        <p className="aqsi-card__state" role="status">
          {deep.text}
        </p>
        <p className="aqsi-caption">
          Пока включён, дополнительно пишется каждый запрос браузера и службы с временем. Скорость продажи он не меняет; персональных
          данных в записи нет. Выключается сам через 30 минут.
        </p>
        {deep.error !== null && <TraceFailure failure={deep.error} />}
        <div className="aqsi-card__actions">
          {deep.enabled ? (
            <Button busy={deep.busy} disabled={deep.busy} onClick={() => actions.setDeep(false)}>
              Выключить
            </Button>
          ) : (
            <Button variant="primary" busy={deep.busy} disabled={deep.busy || !deep.known} onClick={() => actions.setDeep(true)}>
              Включить на 30 минут
            </Button>
          )}
        </div>
      </section>
      <section className="aqsi-panel" aria-labelledby="aqsi-trace-check">
        <h3 id="aqsi-trace-check" className="aqsi-trace__heading">
          Контрольный замер кассы
        </h3>
        <p className="aqsi-caption">
          Показывает, сколько времени команда идёт до кассы и обратно, отдельно от работы над чеком. Сравните с участками продажи.
        </p>
        {check.error !== null && <TraceFailure failure={check.error} />}
        {confirmCheck ? (
          <div role="group" aria-label="Проверить кассу?" className="aqsi-trace__confirm">
            <p className="aqsi-card__text">Это отправит кассе пять служебных чтений. Без печати и без чека. Продолжить?</p>
            <div className="aqsi-card__actions">
              <Button
                variant="primary"
                icon={<Gauge size={14} aria-hidden="true" />}
                onClick={() => {
                  setConfirmCheck(false)
                  actions.runRegisterCheck()
                }}
              >
                Отправить пять чтений
              </Button>
              <Button variant="ghost" onClick={() => setConfirmCheck(false)}>
                Отмена
              </Button>
            </div>
          </div>
        ) : (
          <div className="aqsi-card__actions">
            <Button icon={<Gauge size={14} aria-hidden="true" />} busy={check.busy} disabled={check.busy} onClick={() => setConfirmCheck(true)}>
              Проверить кассу
            </Button>
          </div>
        )}
        {check.runs.length > 0 && (
          <>
            <ol className="aqsi-trace__runs" aria-label="Результат замера кассы">
              {check.runs.map((run) => (
                <li key={run.id}>{run.text}</li>
              ))}
            </ol>
            {check.medianText !== null && <p className="aqsi-trace__total-line">{check.medianText}</p>}
          </>
        )}
      </section>
    </div>
  )
}

function TraceFailure({ failure }: { failure: { message: string; meta: string } }) {
  return (
    <div role="alert" className="aqsi-trace__failure">
      <p className="aqsi-card__text">{failure.message}</p>
      <p className="aqsi-caption">{failure.meta}</p>
      <DiagnosticCopy text={`${failure.message}
${failure.meta}`} />
    </div>
  )
}

function TraceList({ vm, actions }: { vm: TraceScreenVm; actions: TraceScreenActions }) {
  return (
    <>
      <QueryState
        query={vm.list.query}
        what="Продажи"
        emptyText="Продаж с трассировкой пока нет. Они появятся после первой продажи кассира с правом диагностики."
        onRetry={actions.reloadList}
      />
      {vm.list.rows.length > 0 && (
        <ul className="aqsi-trace__list" aria-label="Последние продажи">
          {vm.list.rows.map((row) => (
            <li key={row.saleId}>
              <button
                type="button"
                className="aqsi-trace__sale"
                aria-pressed={row.selected}
                onClick={() => actions.select(row.saleId)}
              >
                <span className="aqsi-trace__when">{row.startedText}</span>
                <span className="aqsi-trace__total">
                  <Clock3 size={14} aria-hidden="true" /> {row.totalText}
                </span>
                <span className="aqsi-trace__outcome">{row.outcome}</span>
                <span className="aqsi-trace__dominant">больше всего: {row.dominantText}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </>
  )
}

function TraceSale({ sale, actions }: { sale: TraceSaleVm; actions: TraceScreenActions }) {
  return (
    <div className="aqsi-trace__sale-view">
      <div className="aqsi-card__actions">
        <Button variant="ghost" icon={<ArrowLeft size={14} aria-hidden="true" />} onClick={actions.closeSale}>
          К списку продаж
        </Button>
        <Button icon={<RefreshCw size={14} aria-hidden="true" />} busy={sale.query.refreshing} onClick={actions.reloadSale}>
          Обновить разбор
        </Button>
      </div>
      <h3 className="aqsi-trace__heading">Продажа {sale.saleId}</h3>
      <QueryState
        query={sale.query}
        what="Разбор продажи"
        emptyText="По этой продаже нет данных трассировки."
        onRetry={actions.reloadSale}
      />
      {sale.waterfall.length > 0 && (
        <>
          <p className="aqsi-trace__total-line">Всего: {sale.totalText}</p>
          {sale.summaryText !== null && (
            <p className="aqsi-trace__summary" role="status">
              <Star size={14} aria-hidden="true" /> {sale.summaryText}
            </p>
          )}
          <Waterfall sale={sale} />
          <Legend />
          {sale.networkText !== null && <p className="aqsi-caption">{sale.networkText}</p>}
          <Points sale={sale} />
          <RegisterSettings sale={sale} />
          {sale.detailCount > 0 && <p className="aqsi-caption">Записано событий подробного режима: {sale.detailCount}.</p>}
          {sale.report !== null && <DiagnosticCopy text={sale.report} label="Скопировать отчёт" />}
        </>
      )}
    </div>
  )
}

function Waterfall({ sale }: { sale: TraceSaleVm }) {
  return (
    <ol className="aqsi-trace__waterfall" aria-label="Участки времени продажи">
      {sale.waterfall.map((row) => (
        <li key={row.key} className="aqsi-trace__segment" data-segment={row.key} data-dominant={row.dominant}>
          <span className="aqsi-trace__segment-name">
            <span className="aqsi-trace__mark" aria-hidden="true">
              {SEGMENT_MARKS[row.key] ?? '·'}
            </span>
            {row.label}
          </span>
          <span className="aqsi-trace__track" aria-hidden="true">
            <span className="aqsi-trace__bar" style={{ marginInlineStart: `${row.offsetPercent}%`, width: `${row.widthPercent}%` }} />
          </span>
          <span className="aqsi-trace__segment-time">
            {row.durationText} · {row.shareText}
            {row.dominant && <strong className="aqsi-trace__top"> · больше всего</strong>}
          </span>
        </li>
      ))}
    </ol>
  )
}

function Legend() {
  return (
    <details className="aqsi-trace__legend">
      <summary>Что значит каждый участок</summary>
      <dl className="aqsi-facts">
        {Object.entries(SEGMENT_WORDS).map(([key, words]) => (
          <div key={key} style={{ display: 'contents' }}>
            <dt>
              {SEGMENT_MARKS[key]} · {words.label}
            </dt>
            <dd>{words.hint}</dd>
          </div>
        ))}
      </dl>
    </details>
  )
}

function Points({ sale }: { sale: TraceSaleVm }) {
  if (sale.points.length === 0) return null
  return (
    <details className="aqsi-trace__points">
      <summary>Контрольные точки ({sale.points.length})</summary>
      <table className="aqsi-trace__table">
        <thead>
          <tr>
            <th scope="col">Когда</th>
            <th scope="col">Откуда</th>
            <th scope="col">Что</th>
            <th scope="col">Длительность</th>
            <th scope="col">Итог</th>
          </tr>
        </thead>
        <tbody>
          {sale.points.map((point) => (
            <tr key={point.id}>
              <td>{point.offsetText}</td>
              <td>{point.sourceText}</td>
              <td>{point.stageText}</td>
              <td>{point.durationText}</td>
              <td>{point.outcome}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </details>
  )
}

function RegisterSettings({ sale }: { sale: TraceSaleVm }) {
  if (sale.settings.length === 0) return null
  return (
    <section className="aqsi-trace__settings" aria-labelledby="aqsi-trace-settings">
      <h4 id="aqsi-trace-settings">Настройки кассы</h4>
      <dl className="aqsi-facts">
        {sale.settings.map((setting) => (
          <div key={setting.key} style={{ display: 'contents' }}>
            <dt>{setting.label}</dt>
            <dd>
              {setting.valueText}
              {setting.slowsNote !== null && <span className="aqsi-trace__slows"> — {setting.slowsNote}</span>}
            </dd>
          </div>
        ))}
      </dl>
      <p className="aqsi-caption">
        Пометка «может замедлять» — предположение по устройству кассы, не результат измерения. Менять настройки кассы — только в
        кабинете AQSI и по решению владельца.
      </p>
    </section>
  )
}

function TraceTrend({ trend }: { trend: TraceTrendVm | null }) {
  if (trend === null) return null
  const max = Math.max(...trend.values, 1)
  const step = trend.values.length > 1 ? 100 / (trend.values.length - 1) : 0
  const points = trend.values.map((value, index) => `${index * step},${30 - (value / max) * 28}`).join(' ')
  const label = `Доставка кабинет → касса за сутки: типично ${trend.medianText}, самая долгая ${trend.maxText}`
  return (
    <section className="aqsi-trace__trend" aria-label="Доставка кабинет → касса за сутки">
      <h3 className="aqsi-trace__heading">Доставка кабинет → касса за сутки</h3>
      <svg viewBox="0 0 100 32" preserveAspectRatio="none" role="img" aria-label={label} className="aqsi-trace__spark">
        <polyline points={points} fill="none" stroke="currentColor" strokeWidth="1.5" vectorEffect="non-scaling-stroke" />
      </svg>
      <p className="aqsi-caption">
        Типично {trend.medianText}, самая долгая {trend.maxText}. Растёт — виновата касса или её интернет, а не наша программа.
      </p>
    </section>
  )
}
